import { AppError } from '@app/errors/AppError'

import { createSelector } from '@app/utils/createSelector'
import { CurrencyPrinter } from '@app/utils/CurrencyPrinter'
import moment from '@app/utils/moment'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiCurrencyRate } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { currentCurrencyResultSelector } from './currencies'
import { routingDateSelector } from './routing'

const currencyRatesSlice = createReduxSlice<Record<string, { loaded_at: string; list: ApiCurrencyRate[] }>>('currency_rates')

export const currencyRateResultSelector = createSelector(
  [currentCurrencyResultSelector, currencyRatesSlice.selector, routingDateSelector],
  (currencyResult, rates, date) =>
    createLoadable(c => {
      const currency = c.unwrap(currencyResult)
      const code = currency.attributes.iso_code
      const slice = rates?.[code]
      if (!slice || moment(slice.loaded_at).add(30, 'minutes').isBefore(date)) {
        const timeString = date.format()
        return c.load(
          createPromise(`Currencies for ${code} must be fetched`, timeString, async dispatch => {
            await dispatch(getCurrencyRatesForCode(code))
          })
        )
      }
      return slice.list
    })
)

export const getCurrencyRatesForCode = (code: string) =>
  createThunk(async dispatch => {
    const { getCurrencyRates } = await import('@app/packages/api/v2/actions/currencies')
    const slice = dispatch(currencyRatesSlice.get())?.[code]
    if (!slice || moment(slice.loaded_at).add(30, 'minutes').isBefore(moment())) {
      const resp = await dispatch(callV2Api(getCurrencyRates(code))).catch(catchFetchDataError(dispatch, 'Currency rates fetch failed'))
      dispatch(currencyRatesSlice.update(s => ({ ...(s ?? {}), [code]: { loaded_at: moment().format(), list: resp.data } })))
    }
    {
      const slice = dispatch(currencyRatesSlice.get())?.[code]
      if (!slice) throw new AppError('Currency rates empty after fetch')
      return slice.list
    }
  })

export const currencyPrinterResultSelector = createSelector([currentCurrencyResultSelector, currencyRateResultSelector], (currencyResult, ratesResult) =>
  createLoadable(c => {
    const rates = c.unwrap(ratesResult)
    const currency = c.unwrap(currencyResult)
    const currentCode = currency.attributes.iso_code
    const ratesDict = Object.fromEntries(rates.filter(r => r.attributes.to === currentCode).map(r => [r.attributes.from, r.attributes.rate]))
    return new CurrencyPrinter(currentCode, ratesDict)
  })
)
